<template>
    <div class="w-[90%] mx-auto text-white py-6">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
            <div>
                <div class="text-[24px] font-bold leading-none">Методички</div>
            </div>
            <button
                class="bg-[#BD9135] hover:bg-[#a47f2f] transition-colors text-black font-semibold px-4 py-2 rounded-[8px] w-max"
                @click="fetchMethodics"
                :disabled="isLoading"
            >
                Оновити список
            </button>
        </div>

        <div
            v-if="canManageMethodics"
            class="bg-[#252526] rounded-[12px] p-5 mb-8 shadow-lg"
        >
            <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
                <div class="text-[18px] font-semibold">
                    {{ editingId ? 'Редагування методички' : 'Нова методичка' }}
                </div>
                <div v-if="editingId" class="text-xs text-gray-400 break-all">
                    ID: {{ editingId }}
                </div>
            </div>

            <div class="grid md:grid-cols-2 gap-4">
                <div class="flex flex-col gap-1">
                    <label class="text-[#BD9135] text-sm font-bold">Назва *</label>
                    <input
                        v-model="form.title"
                        class="bg-[#1E1E1E] border border-[#3a3a3a] focus:border-[#BD9135] transition-colors outline-none text-white text-[16px] w-full py-[10px] px-[12px] rounded-[8px]"
                        placeholder="Введіть назву"
                    >
                </div>
                <div class="flex flex-col gap-1">
                    <label class="text-[#BD9135] text-sm font-bold">Файл {{ editingId ? '' : '*' }}</label>
                    <div class="flex items-center gap-3">
                        <input
                            type="file"
                            @change="onFileChange"
                            class="block text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#BD9135] file:text-black hover:file:bg-[#a47f2f]"
                        >
                        <span v-if="form.fileName" class="text-xs text-gray-400 truncate">{{ form.fileName }}</span>
                    </div>
                </div>
                <div class="md:col-span-2 flex flex-col gap-1">
                    <label class="text-[#BD9135] text-sm font-bold">Опис</label>
                    <textarea
                        v-model="form.description"
                        class="bg-[#1E1E1E] border border-[#3a3a3a] focus:border-[#BD9135] transition-colors outline-none text-white text-[16px] w-full py-[10px] px-[12px] rounded-[8px] min-h-[100px]"
                        placeholder="Додайте короткий опис"
                    />
                </div>
            </div>

            <div class="flex flex-wrap gap-3 mt-4">
                <button
                    class="bg-[#339989] hover:bg-[#2c8073] transition-colors text-white font-semibold px-4 py-2 rounded-[8px]"
                    @click="submitForm"
                    :disabled="isSubmitting"
                >
                    {{ editingId ? 'Оновити методичку' : 'Завантажити методичку' }}
                </button>
                <button
                    v-if="editingId || form.title || form.description || form.file"
                    class="bg-transparent border border-[#3a3a3a] hover:border-[#BD9135] transition-colors text-white font-semibold px-4 py-2 rounded-[8px]"
                    @click="resetForm"
                    :disabled="isSubmitting"
                >
                    Скасувати
                </button>
            </div>
            <div v-if="formError" class="text-[#DA4167] mt-2 text-sm">{{ formError }}</div>
            <div v-if="infoMessage" class="text-green-400 mt-2 text-sm">{{ infoMessage }}</div>
        </div>

        <div class="bg-[#252526] rounded-[12px] p-5 shadow-lg">
            <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                    <div class="text-[18px] font-semibold">Список методичок</div>
                    <div class="text-xs text-gray-400">Завантажено {{ methodics.length }} елементів</div>
                </div>
                <div class="flex flex-wrap gap-3 items-center">
                    <div class="flex items-center gap-2">
                        <span class="text-sm text-gray-300">Ліміт</span>
                        <input
                            type="number"
                            min="1"
                            v-model.number="limit"
                            @change="onLimitChange"
                            class="w-[80px] bg-[#1E1E1E] border border-[#3a3a3a] focus:border-[#BD9135] transition-colors outline-none text-white text-[14px] py-[6px] px-[10px] rounded-[6px]"
                        >
                    </div>
                    <div class="flex items-center gap-2">
                        <button
                            class="px-3 py-2 rounded-[8px] bg-[#1E1E1E] border border-[#3a3a3a] hover:border-[#BD9135] transition-colors disabled:opacity-40"
                            @click="prevPage"
                            :disabled="page === 1 || isLoading"
                        >
                            Назад
                        </button>
                        <span class="text-sm text-gray-300">Стор. {{ page }}</span>
                        <button
                            class="px-3 py-2 rounded-[8px] bg-[#1E1E1E] border border-[#3a3a3a] hover:border-[#BD9135] transition-colors disabled:opacity-40"
                            @click="nextPage"
                            :disabled="!hasNextPage || isLoading"
                        >
                            Вперед
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="isLoading" class="text-center text-gray-400 py-6">Завантаження...</div>
            <div v-else-if="!methodics.length" class="text-center text-gray-400 py-6">Методичок поки немає</div>
            <div v-else class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <div
                    v-for="methodic in methodics"
                    :key="getId(methodic)"
                    class="bg-[#1E1E1E] border border-[#3a3a3a] rounded-[12px] p-4 flex flex-col gap-3 hover:border-[#BD9135] transition-colors"
                >
                    <div class="flex items-start gap-3">
                        <div class="h-12 w-10 rounded-[10px] bg-gradient-to-br from-[#BD9135] to-[#8a6626] text-black font-bold flex items-center justify-center shadow-inner">
                            <span class="text-[12px]">FILE</span>
                        </div>
                        <div class="flex-1 min-w-0">
                            <div class="font-semibold text-[16px] truncate">
                                {{ methodic.title || 'Без назви' }}
                            </div>
                            <div class="text-xs text-gray-400 truncate">
                                {{ fileLink(methodic) ? 'Файл прикріплено' : 'Немає файла' }}
                            </div>
                        </div>
                    </div>

                    <div class="text-sm text-gray-300 h-[48px] overflow-hidden text-ellipsis whitespace-normal">
                        {{ methodic.description || 'Опис відсутній' }}
                    </div>

                    <div class="flex flex-wrap gap-2 mt-auto">
                        <button
                            class="px-3 py-2 rounded-[8px] bg-[#1E1E1E] border border-[#3a3a3a] hover:border-[#BD9135] transition-colors disabled:opacity-50"
                            @click="openMethodic(methodic)"
                            :disabled="!fileLink(methodic)"
                        >
                            Перегляд
                        </button>
                        <template v-if="canManageMethodics">
                            <button
                                class="px-3 py-2 rounded-[8px] bg-[#BD9135] hover:bg-[#a47f2f] text-black font-semibold transition-colors"
                                @click="startEdit(methodic)"
                            >
                                Редагувати
                            </button>
                            <button
                                class="px-3 py-2 rounded-[8px] bg-[#DA4167] hover:bg-[#c23a57] text-white font-semibold transition-colors"
                                @click="removeMethodic(methodic)"
                            >
                                Видалити
                            </button>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import { useMainStore } from '@/desktopApp/stores/mainStore'
import { deleteMethodic, getMethodic, getMethodics, updateMethodic, uploadMethodic } from '@/desktopApp/services/apiRequests'

export default {
    name: 'Methodics',

    setup() {
        const apiUrl = process.env.VUE_APP_API_URL
        const store = useMainStore()

        const methodics = ref([])
        const page = ref(1)
        const limit = ref(10)
        const lastPageSize = ref(0)
        const isLoading = ref(false)
        const isSubmitting = ref(false)
        const formError = ref('')
        const infoMessage = ref('')
        const editingId = ref(null)
        const canManageMethodics = computed(() => store.trainer_position === 'methodist')

        const form = reactive({
            title: '',
            description: '',
            file: null,
            fileName: ''
        })

        const getId = (methodic) => methodic?.id || methodic?.methodic_id || methodic?.methodicId

        const fileLink = (methodic) => {
            const fileId = methodic?.file_id || methodic?.fileId || methodic?.file || methodic?.filename
            return fileId ? `${apiUrl}static/${fileId}` : ''
        }

        const resetForm = () => {
            form.title = ''
            form.description = ''
            form.file = null
            form.fileName = ''
            editingId.value = null
            formError.value = ''
            infoMessage.value = ''
        }

        const onFileChange = (event) => {
            const file = event.target.files && event.target.files.length ? event.target.files[0] : null
            form.file = file
            form.fileName = file ? file.name : ''
        }

        const validateForm = () => {
            if (!form.title.trim()) {
                formError.value = 'Назва є обовʼязковою'
                return false
            }

            if (!editingId.value && !form.file) {
                formError.value = 'Додайте файл методички'
                return false
            }

            formError.value = ''
            return true
        }

        const fetchMethodics = async () => {
            isLoading.value = true
            try {
                const response = await getMethodics(limit.value, (page.value - 1) * limit.value)
                const list = Array.isArray(response?.methodics) ? response.methodics : Array.isArray(response) ? response : []

                if (!list.length && page.value > 1) {
                    page.value = Math.max(1, page.value - 1)
                    return await fetchMethodics()
                }

                methodics.value = list
                lastPageSize.value = list.length
            } catch (error) {
                console.error('Не вдалось отримати методички', error)
                formError.value = 'Не вдалось отримати методички'
            } finally {
                isLoading.value = false
            }
        }

        const submitForm = async () => {
            if (!canManageMethodics.value) {
                formError.value = 'Недостатньо прав для керування методичками'
                return
            }

            if (!validateForm() || isSubmitting.value) {
                return
            }

            isSubmitting.value = true
            infoMessage.value = ''

            try {
                if (editingId.value) {
                    await updateMethodic(editingId.value, form.title.trim(), form.description, form.file)
                    infoMessage.value = 'Методичку оновлено'
                } else {
                    await uploadMethodic(form.title.trim(), form.description, form.file)
                    infoMessage.value = 'Методичку додано'
                }

                resetForm()
                await fetchMethodics()
            } catch (error) {
                console.error('Помилка збереження методички', error)
                formError.value = 'Не вдалося зберегти методичку'
            } finally {
                isSubmitting.value = false
            }
        }

        const startEdit = async (methodic) => {
            if (!canManageMethodics.value) {
                formError.value = 'Недостатньо прав для керування методичками'
                return
            }

            const id = getId(methodic)
            if (!id) {
                return
            }

            try {
                const fresh = await getMethodic(id)
                const target = fresh?.methodic || fresh || methodic
                editingId.value = id
                form.title = target.title || ''
                form.description = target.description || ''
                form.file = null
                form.fileName = ''
                infoMessage.value = ''
                formError.value = ''
            } catch (error) {
                console.error('Не вдалось отримати методичку', error)
                formError.value = 'Не вдалось отримати методичку для редагування'
            }
        }

        const openMethodic = (methodic) => {
            const link = fileLink(methodic)
            if (link) {
                window.open(link, '_blank')
            }
        }

        const removeMethodic = async (methodic) => {
            if (!canManageMethodics.value) {
                formError.value = 'Недостатньо прав для керування методичками'
                return
            }

            const id = getId(methodic)
            if (!id) {
                return
            }

            const confirmation = window.confirm('Видалити методичку?')
            if (!confirmation) {
                return
            }

            try {
                await deleteMethodic(id)
                if (editingId.value === id) {
                    resetForm()
                }
                await fetchMethodics()
            } catch (error) {
                console.error('Не вдалось видалити методичку', error)
                formError.value = 'Не вдалось видалити методичку'
            }
        }

        const nextPage = async () => {
            if (!hasNextPage.value || isLoading.value) {
                return
            }
            page.value += 1
            await fetchMethodics()
        }

        const prevPage = async () => {
            if (page.value === 1 || isLoading.value) {
                return
            }
            page.value -= 1
            await fetchMethodics()
        }

        const onLimitChange = async () => {
            if (!limit.value || limit.value < 1) {
                limit.value = 1
            }
            page.value = 1
            await fetchMethodics()
        }

        const hasNextPage = computed(() => lastPageSize.value === limit.value)

        onMounted(async () => {
            await fetchMethodics()
        })

        return {
            apiUrl,
            methodics,
            page,
            limit,
            lastPageSize,
            isLoading,
            isSubmitting,
            form,
            formError,
            infoMessage,
            editingId,
            canManageMethodics,
            fileLink,
            getId,
            fetchMethodics,
            submitForm,
            onFileChange,
            resetForm,
            startEdit,
            openMethodic,
            removeMethodic,
            nextPage,
            prevPage,
            onLimitChange,
            hasNextPage
        }
    }
}
</script>

